import React, { useEffect, useState, useRef } from "react";
import * as NGL from "ngl/dist/ngl";
import PropTypes from "prop-types";
import Select from "react-select";
import { Loading } from "../atoms";
import SelectedCondonInfo from "./SelectedCondonInfo";
import { PATHOGENICITY_COLORS_BY_STRING } from "../../common/helpers/Constant";
import HelperText from "../atoms/HelperText";
import CameraExplanations from "./CameraExplanations";
import { pathoArrayType } from "./types/ProteinResults.types";
import { nonPathogenicColors } from "./ProteinViewerColors";
import Documentation from "./Documentation";
import GeneWithRefGenomeLink from "../../common/helpers/GeneWithRefGenomeLink";
import { brightGray } from "../../common/helpers/colors";
import TruncatedSeq from "../TruncatedSeq";

const transformVariantSpec = (variantSpec) => {
  const parts = variantSpec.split("-");

  return (
    <>
      {`${parts[0]}-${parts[1]}-`}
      <TruncatedSeq seq={parts[2]} />
      -
      <TruncatedSeq seq={parts[3]} />
    </>
  );
};

const ProteinResults = ({
  currentProtein,
  transcriptInformation,
  refGenomeName,
  varsomeHost,
  avaibleFileOptions,
  onProteinFileChange,
  displayCurrentVariantCheckbox,
  variantSpec,
  queryType,
  mostValuableUrl,
  currentVariantProtein,
  setIsFullScreen,
  isFullScreen,
}) => {
  const checkboxes =
    queryType !== "gene"
      ? [
          { name: "All Residues" },
          { name: "Pathogenic" },
          { name: "Likely Pathogenic" },
          { name: "Uncertain Significance" },
          { name: "Likely Benign" },
          { name: "Benign" },
          { name: "Current Variant" },
        ]
      : [
          { name: "All Residues" },
          { name: "Pathogenic" },
          { name: "Likely Pathogenic" },
          { name: "Uncertain Significance" },
          { name: "Likely Benign" },
          { name: "Benign" },
        ];
  const textInput = React.useRef(null);
  const [existingStage, setExistingStage] = useState(null);
  const [checkedState, setCheckedState] = useState(
    checkboxes.map((v, i) => {
      if (i === 0) {
        return false;
      }
      return true;
    })
  );
  const [representationsArray, setRepresentationsArray] = useState([]);
  const [selectedCodon, setSelectedCodon] = useState(null);
  const [selectedAtom, setSelectedAtom] = useState(null);
  const [, setChecked] = useState(false);
  const [isCurrentVariantInfoOpen, setIsCurrentVariantInfoOpen] =
    useState(true);
  const isMounted = useRef(false);
  const [variantFilter, setVariantFilter] = useState(true);
  const [isFiltersCollapsed] = useState(false);
  const urlOptions = avaibleFileOptions.map((file) => {
    return {
      label: `SwissModel:${file.template} ${file.from}-${file.to} (number of pathogenic vars: ${file.pathoVariants})`,
      value: file.url,
    };
  });
  const extractDigitsRegex = /\d+/g;
  const [selectedUrl, setSelectedUrl] = useState(
    mostValuableUrl
      ? urlOptions.filter((option) => option.value === mostValuableUrl)[0]
      : urlOptions[0]
  );
  const baseRepresentation = (component) => {
    // bail out if the component does not contain a structure
    if (component.type !== "structure") return;
    // add three representations
    component.addRepresentation("cartoon", {
      color: "pink",
    });
    component.addRepresentation("ball+stick", {
      sele: "water or ion or ligand",
      scale: 0.5,
    });
    component.autoView();
  };

  const highlightSelection = (component) => {
    if (component.type !== "structure") return;
    const {
      benign: currentVariantBenign,
      likely_benign: currentVariantLikelyBenign,
      likely_pathogenic: currentVariantLikelyPathogenic,
      pathogenic: currentVariantPathogenic,
      uncertain_significance: currentVariantProteinUncertain,
    } = currentVariantProtein;
    const currentVariantInfo = [
      ...currentVariantBenign,
      ...currentVariantLikelyBenign,
      ...currentVariantLikelyPathogenic,
      ...currentVariantPathogenic,
      ...currentVariantProteinUncertain,
    ];

    const pathogenic = currentProtein?.pathogenic.map((item) => item?.codon);
    const benign = currentProtein?.benign.map((item) => item?.codon);
    const likelyPathogenic = currentProtein?.likely_pathogenic.map(
      (item) => item?.codon
    );
    const likelyBenign = currentProtein?.likely_benign.map(
      (item) => item?.codon
    );
    const uncertainSignificance = currentProtein?.uncertain_significance.map(
      (item) => item?.codon
    );
    const targetChain = currentProtein?.chain;
    const currentVariantCodons = currentVariantInfo.map((item) => item.codon);

    let selectionPatho;
    let selectionBenign;
    let selectionLikelyBenign;
    let selectionLikelyPathogenic;
    let selectionUncertainSignificance;
    let currentVariantHighlight;

    if (currentVariantCodons.length > 0 && queryType !== "gene") {
      const currentVariantString = currentVariantCodons.join(" or ");
      currentVariantHighlight = `:${targetChain} and (${currentVariantString})`;
    }

    if (benign.length > 0) {
      const benignString = benign.join(" or ");
      selectionBenign = `:${targetChain} and (${benignString})`;
    }

    if (pathogenic.length > 0) {
      const pathoString = pathogenic.join(" or ");
      selectionPatho = `:${targetChain} and (${pathoString})`;
    }

    if (likelyPathogenic.length > 0) {
      const likelyPathogenicString = likelyPathogenic.join(" or ");
      selectionLikelyPathogenic = `:${targetChain} and (${likelyPathogenicString})`;
    }

    if (likelyBenign.length > 0) {
      const selectionLikelyBenignString = likelyBenign.join(" or ");
      selectionLikelyBenign = `:${targetChain} and (${selectionLikelyBenignString})`;
    }

    if (uncertainSignificance.length > 0) {
      const selectionUncertainSignificanceString =
        uncertainSignificance.join(" or ");
      selectionUncertainSignificance = `:${targetChain} and (${selectionUncertainSignificanceString})`;
    }

    const blueSphereResult = targetChain
      ? component.addRepresentation("spacefill", {
          visible: checkedState[0],
          sele: `:${targetChain}`,
          color: "blue",
        })
      : null;
    const uncertainSignificanceSphereResult = selectionUncertainSignificance
      ? component.addRepresentation("spacefill", {
          sele: selectionUncertainSignificance,
          visible: checkedState[3],
          color: PATHOGENICITY_COLORS_BY_STRING["Uncertain Significance"],
        })
      : null;
    const likelyBenignSphereResult = selectionLikelyBenign
      ? component.addRepresentation("spacefill", {
          sele: selectionLikelyBenign,
          visible: checkedState[4],
          color: PATHOGENICITY_COLORS_BY_STRING["Likely Benign"],
        })
      : null;
    const likelyPathogenicSphereResult = selectionLikelyPathogenic
      ? component.addRepresentation("spacefill", {
          sele: selectionLikelyPathogenic,
          visible: checkedState[2],
          color: PATHOGENICITY_COLORS_BY_STRING["Likely Pathogenic"],
        })
      : null;
    const benignSpheresResult = selectionBenign
      ? component.addRepresentation("spacefill", {
          sele: selectionBenign,
          visible: checkedState[5],
          color: PATHOGENICITY_COLORS_BY_STRING.Benign,
        })
      : null;
    const pathogenicSphereResult = selectionPatho
      ? component.addRepresentation("spacefill", {
          sele: selectionPatho,
          visible: checkedState[1],
          color: PATHOGENICITY_COLORS_BY_STRING.Pathogenic,
        })
      : null;
    const highlightedCurrentVariantInfo = currentVariantHighlight
      ? component.addRepresentation("spacefill", {
          visible: checkedState[6],
          sele: currentVariantHighlight,
          color: "magenta",
        })
      : null;
    const newArr = [...representationsArray];
    newArr.push(
      blueSphereResult,
      pathogenicSphereResult,
      likelyPathogenicSphereResult,
      uncertainSignificanceSphereResult,
      likelyBenignSphereResult,
      benignSpheresResult,
      highlightedCurrentVariantInfo
    );
    setRepresentationsArray(newArr);
  };

  const annotate = (component) => {
    baseRepresentation(component);
    highlightSelection(component);
  };

  const handleScroll = (e) => {
    e.preventDefault();
  };

  const handleOnChange = (position) => {
    setCheckedState(
      checkedState.map((item, index) => (index === position ? !item : item))
    );
  };

  useEffect(() => {
    if (!selectedAtom) return;
    setSelectedCodon(selectedAtom.match(extractDigitsRegex)[0]);
  }, [selectedAtom]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (currentProtein) {
      const stage = new NGL.Stage("viewport", {
        backgroundColor: brightGray,
        tooltip: false,
      });

      // the following tow lines are an attempt to fix the background in Chrome
      // (doesn't work; compare swissmodel visualization on chrome and firefox)
      stage.viewer.renderer.setClearColor(0xffffff, 0.01);
      stage.viewer.requestRender();

      stage.loadFile(currentProtein.pdb_file).then(annotate);
      let zoomResidue;
      stage.signals.clicked.add((pickingProxy) => {
        if (pickingProxy && (pickingProxy.atom || pickingProxy.bond)) {
          const atom = pickingProxy.atom || pickingProxy.closestBondAtom;
          setSelectedAtom(atom.qualifiedName());
          zoomResidue = `${atom.resno}:${atom.chainname}`;
        }
      });
      stage.viewer.container.addEventListener("dblclick", () => {
        stage.eachComponent((o) => {
          o.autoView(zoomResidue, 1000);
        });
      });
      const tooltip = document.createElement("div");
      Object.assign(tooltip.style, {
        display: "none",
        position: "absolute",
        zIndex: 99999,
        pointerEvents: "none",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        color: "lightgrey",
        padding: "0.5em",
        fontFamily: "sans-serif",
      });
      stage.viewer.container.appendChild(tooltip);

      // load a structure file

      // listen to `hovered` signal to move tooltip around and change its text
      stage.signals.hovered.add((pickingProxy) => {
        if (pickingProxy && (pickingProxy.atom || pickingProxy.bond)) {
          const atom = pickingProxy.atom || pickingProxy.closestBondAtom;
          const cp = pickingProxy.canvasPosition;
          tooltip.innerText = `Residue: ${atom.qualifiedName()}`;
          tooltip.style.bottom = `${cp.y + 1}px`;
          tooltip.style.left = `${cp.x + 1}px`;
          tooltip.style.display = "block";
        } else {
          tooltip.style.display = "none";
        }
      });
      setExistingStage(stage);
      const proteinViewerRef = textInput.current;

      proteinViewerRef.addEventListener("mousewheel", handleScroll, {
        passive: false,
      });
      window.addEventListener(
        "resize",
        () => {
          stage.handleResize();
        },
        false
      );
      return () => {
        proteinViewerRef.removeEventListener("mousewheel", handleScroll);
      };
    }
  }, []);

  useEffect(() => {
    if (existingStage && representationsArray.length === 0) {
      existingStage.removeAllComponents();
      existingStage.loadFile(currentProtein.pdb_file).then(annotate);
      setIsCurrentVariantInfoOpen(true);
    }
  }, [currentProtein, representationsArray]);

  useEffect(() => {
    if (existingStage && isFullScreen) {
      existingStage.setSize("100%", "650");
      existingStage.autoView(1000);
    } else if (existingStage && !isFullScreen) {
      existingStage.setSize("1200", "500");
      existingStage.autoView(1000);
    }
  }, [isFullScreen]);

  const filterCheckOptionsHandle = (array, value) => {
    for (let i = 1; i < array.length; i++) {
      array[i] = value;
      representationsArray[i]?.setVisibility(value);
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (!checkedState) return;
    const checkedStateCopy = [...checkedState];
    if (
      variantFilter &&
      checkedState.slice(1).every((value) => value === true)
    ) {
      filterCheckOptionsHandle(checkedStateCopy, false);
      setCheckedState(checkedStateCopy);
      return;
    }
    if (
      !variantFilter &&
      checkedState.slice(1).every((value) => value === false)
    ) {
      filterCheckOptionsHandle(checkedStateCopy, true);
      setCheckedState(checkedStateCopy);
      return;
    }
    if (
      variantFilter &&
      !checkedState.slice(1).every((value) => value === true)
    ) {
      filterCheckOptionsHandle(checkedStateCopy, true);
      setCheckedState(checkedStateCopy);
    }
    if (
      !variantFilter &&
      !checkedState.slice(1).every((value) => value === false)
    ) {
      filterCheckOptionsHandle(checkedStateCopy, false);
      setCheckedState(checkedStateCopy);
    }
  }, [variantFilter]);

  const formattedVariantSpec = transformVariantSpec(variantSpec);

  return (
    <div className="varsome-result">
      <div className="varsome-container">
        <div className="varsome-title">
          <span>
            3d Protein Viewer{" "}
            <HelperText RenderContent={() => <CameraExplanations />} />
          </span>
          <div className="varsome-browser-controls varsome-right">
            <Documentation
              documentationUrl="https://docs.varsome.com/en/3d-protein-viewer?hs_preview=oXfpBRWk-66772918605"
              descriptionText="Documentation"
            />
            <a
              className="btn-action tw--m-2 tw-mr-8"
              data-tooltip="Toggle fullscreen"
              style={{
                marginTop: "-8px",
                marginRight: "10px",
                backgroundColor: "white",
              }}
              onClick={() => setIsFullScreen(!isFullScreen)}
            >
              <i className="varsome-icons icon-fullscreen" />
            </a>
          </div>
        </div>
        <div className="tw-ml-4 tw-mt-2 tw-grid tw-grid-cols-3 tw-mb-4">
          <div>
            {transcriptInformation[0]?.gene_symbol && (
              <div className="tw-mt-2">
                Gene:{" "}
                <GeneWithRefGenomeLink
                  href={`${varsomeHost}/gene/${transcriptInformation[0]?.gene_symbol}`}
                  target="_blank"
                >
                  {transcriptInformation[0]?.gene_symbol}
                </GeneWithRefGenomeLink>
              </div>
            )}
            {currentProtein?.uniprot && (
              <div className="tw-mt-1">
                <a
                  href={`https://www.uniprot.org/uniprot/${currentProtein?.uniprot}`}
                  target="_blank"
                >
                  View on UniProt
                </a>
              </div>
            )}
            {selectedUrl.value && (
              <div className="tw-mt-1">
                <a
                  href={`https://swissmodel.expasy.org/templates/${selectedUrl.value
                    .split("/")[6]
                    .split("_")[2]
                    .replace(/\.[a-zA-Z]+/, "")}`}
                  target="_blank"
                >
                  View on SwissModel
                </a>
              </div>
            )}
            {transcriptInformation[0]?.name && (
              <div className="tw-mt-1">
                Transcript:{" "}
                <a
                  href={`${varsomeHost}/transcript/${refGenomeName}/${transcriptInformation[0].name}`}
                  target="_blank"
                >
                  {transcriptInformation[0]?.name}
                </a>
              </div>
            )}
          </div>
          <div>
            <h4>Select protein structure</h4>
            <Select
              styles={{
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
              className="tw-w-72"
              options={urlOptions}
              value={selectedUrl.value ? { label: selectedUrl.label } : ""}
              onChange={(e) => {
                setSelectedUrl({ value: e.value, label: e.label });
                onProteinFileChange(e);
                if (representationsArray.length > 0) {
                  setRepresentationsArray([]);
                }
                setChecked(false);
              }}
            />
          </div>
        </div>
        <div className={isFullScreen ? "tw-grid tw-grid-cols-2 tw-ml-4" : ""}>
          <div>
            <div className="tw-flex tw-w-full tw-relative">
              <div
                id="viewport"
                style={
                  isFullScreen
                    ? { width: "100%", height: "650px" }
                    : { width: "1200px", height: "500px" }
                }
                ref={textInput}
              />
              {representationsArray.length > 0 && (
                <>
                  <div
                    className="varsome-genome-browser-legend ignore-mouse tw-mt-2 tw-absolute tw-overflow-auto"
                    style={{
                      zIndex: 1,
                      width: "185px",
                      bottom: "auto",
                      height: queryType === "gene" ? "160px" : "180px",
                    }}
                  >
                    <div className="tw-cursor-pointer ignore-mouse">
                      <strong className=" ignore-mouse">Show</strong>
                    </div>
                    {!isFiltersCollapsed && (
                      <div className="mt-1 tw-absolute">
                        {checkboxes.map((item, index) => {
                          return (
                            <>
                              {index !== 0 ? (
                                <div
                                  key={index}
                                  className="tw-flex tw-items-center tw-text-xs ignore-mouse tw-ml-2"
                                  onClick={() => {
                                    handleOnChange(index);
                                    representationsArray[index]?.setVisibility(
                                      !checkedState[index]
                                    );
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    className="tw-align-middle tw-pt-1 tw-cursor-pointer ignore-mouse"
                                    onChange={() => {
                                      handleOnChange(index);
                                      representationsArray[
                                        index
                                      ]?.setVisibility(!checkedState[index]);
                                    }}
                                    checked={checkedState[index]}
                                  />
                                  <label
                                    key={`${item}-${index}`}
                                    className="tw-flex tw-items-center tw-m-0 tw-pl-1 tw-cursor-pointer tw-align-middle ignore-mouse"
                                  >
                                    <div
                                      className="tw-inline-block tw-pr-1 tw-pt-1 tw-rounded-full ignore-mouse"
                                      style={{
                                        width: "10px",
                                        height: "10px",
                                        backgroundColor:
                                          PATHOGENICITY_COLORS_BY_STRING[
                                            item.name
                                          ] || nonPathogenicColors[item.name],
                                      }}
                                    />
                                    <span className="tw-pl-1 ignore-mouse">
                                      {item.name}
                                    </span>
                                  </label>
                                </div>
                              ) : (
                                <>
                                  <div
                                    key={index}
                                    className="tw-flex tw-items-center tw-text-xs ignore-mouse"
                                    onClick={() => {
                                      handleOnChange(index);
                                      representationsArray[
                                        index
                                      ]?.setVisibility(!checkedState[index]);
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="tw-align-middle tw-pt-1 tw-cursor-pointer ignore-mouse"
                                      onChange={() => {
                                        handleOnChange(index);
                                        representationsArray[
                                          index
                                        ]?.setVisibility(!checkedState[index]);
                                      }}
                                      checked={checkedState[index]}
                                    />
                                    <label
                                      key={`${item}-${index}`}
                                      className="tw-flex tw-items-center tw-m-0 tw-pl-1 tw-cursor-pointer tw-align-middle ignore-mouse"
                                    >
                                      <div
                                        className="tw-inline-block tw-pr-1 tw-pt-1 tw-rounded-full ignore-mouse"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                          backgroundColor:
                                            PATHOGENICITY_COLORS_BY_STRING[
                                              item.name
                                            ] || nonPathogenicColors[item.name],
                                        }}
                                      />
                                      <span className="tw-pl-1 ignore-mouse">
                                        {item.name}
                                      </span>
                                    </label>
                                  </div>
                                  <div
                                    key={`${index}-variant-filter`}
                                    className="tw-flex tw-items-center tw-text-xs ignore-mouse"
                                    onClick={() =>
                                      setVariantFilter(!variantFilter)
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      className="tw-align-middle tw-pt-1 tw-cursor-pointer ignore-mouse"
                                      onChange={() =>
                                        setVariantFilter(!variantFilter)
                                      }
                                      checked={checkedState
                                        .slice(1)
                                        .some((value) => value === true)}
                                    />
                                    <label
                                      key={`${item}-${index}`}
                                      className="tw-flex tw-items-center tw-m-0 tw-pl-1 tw-cursor-pointer tw-align-middle ignore-mouse"
                                      onClick={() =>
                                        setVariantFilter(!variantFilter)
                                      }
                                    >
                                      <span className="tw-pl-1 ignore-mouse">
                                        Variants
                                      </span>
                                    </label>
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {!displayCurrentVariantCheckbox && (
              <>
                {isCurrentVariantInfoOpen && queryType !== "gene" && (
                  <div
                    className={`tw-mt-2 ${
                      isFullScreen ? "" : "tw-ml-4"
                    } tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-relative tw-w-3/5 tw-mb-2"
                    role="alert`}
                  >
                    {/* <strong className="font-bold">Holy smokes!</strong> */}
                    <span className="tw-block tw-sm:inline">
                      This protein structure does not contain any data related
                      to the variant {formattedVariantSpec}.
                    </span>
                    <span className="tw-absolute tw-top-0 tw-bottom-0 tw-right-0 tw-px-4 tw-py-3">
                      <svg
                        className="tw-fill-current tw-h-6 tw-w-6 tw-text-red-500"
                        role="button"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        onClick={() => setIsCurrentVariantInfoOpen(false)}
                      >
                        <title>Close</title>
                        <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                      </svg>
                    </span>
                  </div>
                )}
              </>
            )}
            {!representationsArray.length && (
              <div className="tw-text-center">
                <h4>Loading please wait...</h4>
                <Loading classNames="fa-4x" />
              </div>
            )}
          </div>

          <SelectedCondonInfo
            isFullScreen={isFullScreen}
            selectedCodon={selectedCodon}
            currentProtein={currentProtein}
            currentAtom={selectedAtom}
          />
        </div>
      </div>
    </div>
  );
};

ProteinResults.propTypes = {
  currentProtein: PropTypes.shape({
    chain: PropTypes.string,
    likely_pathogenic: pathoArrayType,
    pathogenic: pathoArrayType,
    likely_benign: pathoArrayType,
    benign: pathoArrayType,
    uncertain_significance: pathoArrayType,
    uniprot: PropTypes.string,
    pdb_file: PropTypes.string,
  }),
  refGenomeName: PropTypes.string,
  varsomeHost: PropTypes.string,
  transcriptInformation: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      strand: PropTypes.string,
      coding_impact: PropTypes.string,
      function: PropTypes.arrayOf(PropTypes.string),
      hgvs: PropTypes.string,
      hgvs_p1: PropTypes.string,
      hgvs_p3: PropTypes.string,
      location: PropTypes.string,
      coding_location: PropTypes.string,
      canonical: PropTypes.bool,
      gene_symbol: PropTypes.string,
      splice_distance: PropTypes.string,
      ensembl_support_level: PropTypes.string,
    })
  ),
  avaibleFileOptions: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      from: PropTypes.string,
      url: PropTypes.string,
      template: PropTypes.string,
      pathoVariants: PropTypes.number,
    })
  ),
  currentVariantProtein: PropTypes.shape({
    benign: pathoArrayType,
    likely_benign: pathoArrayType,
    pathogenic: pathoArrayType,
    likely_pathogenic: pathoArrayType,
    uncertain_significance: pathoArrayType,
    to: PropTypes.string,
    from: PropTypes.string,
    uniprot: PropTypes.string,
    template: PropTypes.string,
    pdb_file: PropTypes.string,
  }),
  onProteinFileChange: PropTypes.func.isRequired,
  displayCurrentVariantCheckbox: PropTypes.bool.isRequired,
  variantSpec: PropTypes.string,
  queryType: PropTypes.string,
  mostValuableUrl: PropTypes.string,
  setIsFullScreen: PropTypes.func.isRequired,
  isFullScreen: PropTypes.bool,
};

ProteinResults.defaultProps = {
  currentProtein: null,
  refGenomeName: "",
  varsomeHost: "",
  transcriptInformation: null,
  avaibleFileOptions: null,
  variantSpec: "",
  queryType: "",
  currentVariantProtein: null,
  mostValuableUrl: null,
  isFullScreen: false,
};

export default ProteinResults;
